import { ServiceItem } from '../types/service.types';

// Environment-based API URLs
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
export const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL || '';

// Strapi CMS base for content (e.g. the global announcement banner). Defaults
// to CMS_BASE_URL — the exact base the app already uses to reach Strapi for
// store data (see API_ENDPOINTS.STORES) — so the banner fetch follows the same
// proven origin/CORS path. Override with REACT_APP_STRAPI_BASE_URL if the
// announcement content ever moves to a different Strapi instance.
export const STRAPI_BASE_URL = process.env.REACT_APP_STRAPI_BASE_URL ?? CMS_BASE_URL;

// Only pass environment param to remote CMS — local Strapi doesn't accept it
const isLocalCMS = CMS_BASE_URL.includes('localhost') || CMS_BASE_URL.includes('127.0.0.1');

// Which backend environment this BUILD talks to. Derived from the API base
// the bundle was compiled with — the app that calls apistg IS the staging
// app, whatever hostname it is served on. This used to be inferred from
// window.location against the single hostname store.mypetgroomer.com.au,
// which meant every OTHER staging tenant hostname (abc.mypetgroomer.com.au,
// …) declared itself production and had its tenant lookup answered 404.
// The CMS now routes the rest (sign-in, bookings) by whichever database has
// this storefront's address (mpg-publicsite G6/D5): they agree as long as
// this build's API is the environment where the domain is registered.
export const API_ENVIRONMENT: string | undefined = isLocalCMS
  ? undefined
  : API_BASE_URL.includes('apistg')
    ? 'staging'
    : 'production';

/**
 * Query fragment carrying the environment to CMS tenant lookups, '' when
 * talking to a local Strapi. Every `by-domain-name` call must append this —
 * without it the CMS guesses from Origin, and only the one legacy hostname
 * guesses right.
 */
export const CMS_ENVIRONMENT_QUERY = API_ENVIRONMENT ? `&environment=${API_ENVIRONMENT}` : '';

interface ApiConfig {
  baseURL: string;
  companyId: string;
  headers: {
    'accept': string;
  };
}

export const API_CONFIG: ApiConfig = {
  baseURL: API_BASE_URL,
  companyId: '1',
  headers: {
    'accept': 'text/plain'
  }
} as const;

export const API_ENDPOINTS = {
  STORES: `${CMS_BASE_URL}/api/companies/by-domain-name`,
  IMAGE_PROXY: `${API_BASE_URL}/proxy/image`,
  ANNOUNCEMENT: `${STRAPI_BASE_URL}/api/announcement`,
} as const;

// ===============================
// CACHE SERVICE & OPTIMIZED API
// ===============================

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes

  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }
    return item.data;
  }

  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  getStats(): { size: number; keys: string[] } {
    return { size: this.cache.size, keys: Array.from(this.cache.keys()) };
  }

  cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];
    this.cache.forEach((item, key) => {
      if (now - item.timestamp > item.ttl) expiredKeys.push(key);
    });
    expiredKeys.forEach(key => this.cache.delete(key));

  }
}

export const cacheService = new CacheService();

// Auto-cleanup every 2 minutes
setInterval(() => cacheService.cleanup(), 2 * 60 * 1000);

// Debug access
(window as any).cacheService = cacheService;

// ===============================
// OPTIMIZED API SERVICE
// ===============================

interface ServiceResponse {
  id: number;
  name: string;
  description: string;
  colorHexCode: string;
  // Admin-defined display order for the category (lower shown first).
  sortOrder?: number;
  groomingServiceTypeDto: number;
  groomingServicePricingOptions?: Array<{
    id: number;
    price: number;
    weightFrom?: number;
    weightTo?: number;
    breedGroupId?: number;
    note?: string;
    pricingOptionDuration?: number;
    // The salon's order (part 9); absent until the admin sends it.
    sortOrder?: number | null;
  }>;
  groomingServices?: Array<{
    id: number;
    name: string;
    description: string | null;
    // Admin-defined display order within the category (lower shown first).
    sortOrder?: number;
    groomingServicePricingOptions?: Array<{
      id: number;
      price: number;
      weightFrom?: number;
      weightTo?: number;
      breedGroupId?: number;
      note?: string;
      pricingOptionDuration?: number;
      // The salon's order (part 9); absent until the admin sends it.
      sortOrder?: number | null;
    }>;
    groomingServiceAppliedStoresAndUsers?: Array<{
      storeId: number;
      userIds: number[];
    }>;
  }>;
}

interface BreedGroup {
  id: number;
  name: string;
  breeds: number[];
}

interface Breed {
  id: number;
  name: string;
  petType: number;
  /** Its other names ("Cavapoo" for Cavoodle): MPG's, and this salon's own. */
  alsoCalled?: string[];
}

interface CategoryAvailability {
  dog: boolean;
  cat: boolean;
  accommodation: boolean;
}

/** One Popular read (phase 9c): a location, a pet type (1 cat, 2 dog), and the pet's breed or a service when asked. */
export interface PopularQuery {
  storeId: number;
  petType: 1 | 2;
  breedName?: string | null;
  serviceId?: number | null;
}

/**
 * How long the menu read (`/api/grooming-service-categories/by-filter`, by location and pet type) is kept:
 * one TTL for its one cache key, whichever caller reads it first (`fetchDetailedGroomingServices`).
 */
export const MENU_TTL_MS = 10 * 60 * 1000;

const popularKey = (q: PopularQuery) =>
  `${q.storeId}:${q.petType}:${(q.breedName ?? '').trim().replace(/\s+/g, ' ').toLowerCase()}:${q.serviceId ?? ''}`;

class OptimizedApiService {
  private readonly baseUrl = CMS_BASE_URL;
  /**
   * Reads under way, by cache key: a second caller of the same read shares
   * it. The services step asked for the dog menu twice at once (the menu and
   * the dog/cat availability check), and the home page's preload a third
   * time, because the cache only fills once a read has answered.
   */
  private readonly inflight = new Map<string, Promise<any>>();

  private getCacheKey(endpoint: string, params: Record<string, any> = {}): string {
    const paramString = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `api:${endpoint}${paramString ? `?${paramString}` : ''}`;
  }

  private shared<T>(cacheKey: string, read: () => Promise<T>): Promise<T> {
    const running = this.inflight.get(cacheKey);
    if (running) return running;
    const promise = read().finally(() => this.inflight.delete(cacheKey));
    this.inflight.set(cacheKey, promise);
    return promise;
  }

  private async fetchWithCache<T>(
    endpoint: string,
    params: Record<string, any> = {},
    ttl: number = 5 * 60 * 1000
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, params);

    const cachedData = cacheService.get<T>(cacheKey);
    if (cachedData) return cachedData;
    return this.shared(cacheKey, () => this.readAndCache<T>(endpoint, params, cacheKey, ttl));
  }

  private async readAndCache<T>(endpoint: string, params: Record<string, any>, cacheKey: string, ttl: number): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value.toString());
      }
    });

    try {
      const response = await fetch(url.toString(), {
        headers: API_CONFIG.headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      cacheService.set(cacheKey, data, ttl);
      return data;
    } catch (error) {
      console.error(`❌ API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  async fetchServiceCategories(storeId: number, serviceTypeDto: number): Promise<ServiceItem[]> {
    // The menu read, kept as the step keeps it (one TTL for the one key, MENU_TTL_MS).
    const data = await this.fetchDetailedGroomingServices(storeId, serviceTypeDto);

    if (!Array.isArray(data)) {
      throw new Error('Invalid data format received');
    }

    return data.map((service: ServiceResponse) => ({
      id: service.id || 0,
      name: service.name || '',
      description: service.description || '',
      colorHexCode: service.colorHexCode || '',
      groomingServiceTypeDto: serviceTypeDto,
      groomingServicePricingOptions: service.groomingServicePricingOptions || []
    }));
  }

  private menuKey(storeId: number, serviceTypeDto: number): string {
    return this.getCacheKey('/api/grooming-service-categories/by-filter', { storeId, groomingServiceTypeDto: serviceTypeDto, environment: API_ENVIRONMENT });
  }

  /**
   * The last menu this page read for the location and pet type, kept in memory for the page (it is the
   * salon's public menu, nothing personal): a pet switch, or a return to the step after the cache has run
   * out, shows it at once while it is read again (GUARDRAILS G33). Forgotten with the cache when the API
   * refuses a line as not on the menu (`forgetDetailedGroomingServices`, G30).
   */
  private readonly menuKept = new Map<string, ServiceResponse[]>();

  /**
   * The menu when this page has it: the cache, else the last answer kept (`menuKept`); null when it has
   * neither. A return to the step, a pet switch and the home page's preload show it on the first frame.
   */
  peekDetailedGroomingServices(storeId: number, serviceTypeDto: number): ServiceResponse[] | null {
    const key = this.menuKey(storeId, serviceTypeDto);
    return cacheService.get<ServiceResponse[]>(key) ?? this.menuKept.get(key) ?? null;
  }

  /**
   * The menu read, the one way every caller reads the menu key (the step, the dog/cat check, the home
   * page's preload), so it is kept MENU_TTL_MS whoever reads it first: the dog/cat check used to keep it
   * 2 minutes and the preload 5, and the warm-up's check runs first, so the step's 10 minutes were 2.
   */
  async fetchDetailedGroomingServices(storeId: number, serviceTypeDto: number): Promise<ServiceResponse[]> {
    const data = await this.fetchWithCache<ServiceResponse[]>(
      '/api/grooming-service-categories/by-filter',
      { storeId, groomingServiceTypeDto: serviceTypeDto, environment: API_ENVIRONMENT },
      MENU_TTL_MS
    );
    if (Array.isArray(data)) this.menuKept.set(this.menuKey(storeId, serviceTypeDto), data);
    return data;
  }

  /**
   * Forget the menu kept for this location and pet type, so the next read is
   * the salon's menu as it is now: the API refused a line as not on it, or
   * not the business's (G30; mpgadmin G119, G120), so what this page kept is
   * out of date.
   */
  forgetDetailedGroomingServices(storeId: number, serviceTypeDto: number): void {
    const key = this.menuKey(storeId, serviceTypeDto);
    cacheService.delete(key);
    this.menuKept.delete(key);
  }

  async checkCategoryAvailability(storeId: number): Promise<CategoryAvailability> {
    const cacheKey = `availability:store:${storeId}`;
    const cached = cacheService.get<CategoryAvailability>(cacheKey);
    if (cached) return cached;

    const serviceTypes = { dog: 2, cat: 1, accommodation: 3 };
    const results: CategoryAvailability = { dog: false, cat: false, accommodation: false };

    const promises = Object.entries(serviceTypes).map(async ([category, typeId]) => {
      try {
        // The step's own menu read (the same key), so it is kept as long as the step keeps it.
        const data = await this.fetchDetailedGroomingServices(storeId, typeId);
        // Check if there are any categories with actual groomingServices
        const hasServices = Array.isArray(data) && data.some(
          cat => cat.groomingServices && cat.groomingServices.length > 0
        );
        return { category, available: hasServices, known: true };
      } catch (error) {
        // Unknown is not "none": a request that failed must not hide a pet
        // type the store does sell. Fail open, and don't remember the guess.
        console.error(`Error checking ${category} services:`, error);
        return { category, available: true, known: false };
      }
    });

    const availabilityResults = await Promise.all(promises);
    availabilityResults.forEach(({ category, available }) => {
      (results as any)[category] = available;
    });

    if (availabilityResults.every(r => r.known)) {
      cacheService.set(cacheKey, results, 5 * 60 * 1000);
    }
    return results;
  }

  async fetchBreedGroups(): Promise<BreedGroup[]> {
    return this.fetchBreedGroupsPublic();
  }

  private async fetchBreedGroupsPublic(): Promise<BreedGroup[]> {
    // This storefront's business — breeds are per business (was hard-coded 1).
    const { storeService } = await import('../services/store.service');
    const companyId = await storeService.getCompanyId();
    const cacheKey = this.getCacheKey('/api/breed-groups/by-company-id', { companyId, environment: API_ENVIRONMENT });

    const cachedData = cacheService.get<BreedGroup[]>(cacheKey);
    if (cachedData) return cachedData;

    const url = new URL(`${this.baseUrl}/api/breed-groups/by-company-id`);
    url.searchParams.append('companyId', String(companyId));
    if (API_ENVIRONMENT) url.searchParams.append('environment', API_ENVIRONMENT);

    return this.shared(cacheKey, async () => {
      try {
        const response = await fetch(url.toString(), {
          headers: {
            'accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const ttl = 30 * 60 * 1000; // 30 minutes cache
        cacheService.set(cacheKey, data, ttl);
        return data;
      } catch (error) {
        console.error(`❌ API Error for /api/breed-groups/by-company-id:`, error);
        throw error;
      }
    });
  }

  /**
   * The salon's booking site menu (phase 8, SF 39): its own pills, or the API's
   * starting menu. Public and per business, read as the breed groups are: this
   * storefront's company (G7), the environment, the same CMS. Kept 10 minutes,
   * as the menu read is. The body is checked by `utils/siteMenu.ts`
   * `readSiteMenu`; a failure (until the API is released the CMS answers 404)
   * isn't kept and isn't logged as an error: the services step shows today's
   * bar (G28).
   */
  async fetchBookingSiteMenu(): Promise<unknown> {
    const { storeService } = await import('../services/store.service');
    const companyId = await storeService.getCompanyId();
    const cacheKey = this.getCacheKey('/api/booking-site-menu', { companyId, environment: API_ENVIRONMENT });
    this.siteMenuKey = cacheKey;
    const cached = cacheService.get<unknown>(cacheKey);
    if (cached) return cached;
    // A failure is remembered for a minute, in memory only, so the warm-up's
    // failed read (a 404 until the API is released) isn't read again when the
    // step mounts; a later visit tries again.
    const failedAt = this.menuFailedAt.get(cacheKey);
    if (failedAt !== undefined && Date.now() - failedAt < 60 * 1000) throw new Error('booking-site-menu unavailable');

    // Shared with a read under way: the services step's warm-up starts it before the step mounts.
    return this.shared(cacheKey, async () => {
      try {
        const url = new URL(`${this.baseUrl}/api/booking-site-menu`);
        url.searchParams.append('companyId', String(companyId));
        if (API_ENVIRONMENT) url.searchParams.append('environment', API_ENVIRONMENT);
        const response = await fetch(url.toString(), { headers: { accept: 'application/json' } });
        if (!response.ok) throw new Error(`booking-site-menu ${response.status}`);
        const data = await response.json();
        cacheService.set(cacheKey, data, 10 * 60 * 1000);
        this.menuFailedAt.delete(cacheKey);
        return data;
      } catch (e) {
        this.menuFailedAt.set(cacheKey, Date.now());
        throw e;
      }
    });
  }

  /** When the booking site menu's read last failed, by cache key (`fetchBookingSiteMenu`). */
  private readonly menuFailedAt = new Map<string, number>();

  /** The cache key of this storefront's booking site menu, once a read has named the business. */
  private siteMenuKey: string | null = null;

  /**
   * The booking site menu when this page already knows it, for the step's first frame (a return to the
   * step draws its pills at once, G33): `{ body }` for an answer kept, `{ failed: true }` for a read that
   * failed under a minute ago (the step then draws today's bar), undefined when it isn't known.
   */
  peekBookingSiteMenu(): { body: unknown } | { failed: true } | undefined {
    const key = this.siteMenuKey;
    if (!key) return undefined;
    const cached = cacheService.get<unknown>(key);
    if (cached) return { body: cached };
    const failedAt = this.menuFailedAt.get(key);
    return failedAt !== undefined && Date.now() - failedAt < 60 * 1000 ? { failed: true } : undefined;
  }

  /**
   * Popular (phase 9c, SF 41, 61): what this salon's clients book most at a
   * location, for a pet type, with the pet's breed rank (`breedName`) and,
   * for a service (`serviceId`), what is booked with it. The CMS's public
   * `GET /api/popular` (mpg-publicsite #160, CMS G20), read as the booking
   * site's other public reads are: this storefront's business (G7), the
   * environment, the same CMS. Ids and numbers only (`utils/popular.ts`
   * `readPopular` checks the body).
   *
   * Kept 10 minutes in memory by location, pet type, breed and service (a
   * location id belongs to one business), so the warm-up's read, the step's
   * and a pet switched back to are one. A failure (a 404 until the CMS route
   * is released, a 502) isn't kept, is remembered for a minute so the step
   * doesn't read again what the warm-up just failed, and isn't logged as an
   * error: the step shows no Popular (G29).
   */
  peekPopular(q: PopularQuery): unknown | undefined {
    const hit = this.popularKept.get(popularKey(q));
    return hit && Date.now() - hit.at < 10 * 60 * 1000 ? hit.data : undefined;
  }

  /** Whether this read failed under a minute ago (`fetchPopular` would throw at once): the step shows none without waiting. */
  popularFailed(q: PopularQuery): boolean {
    const failedAt = this.popularFailedAt.get(popularKey(q));
    return failedAt !== undefined && Date.now() - failedAt < 60 * 1000;
  }

  async fetchPopular(q: PopularQuery): Promise<unknown> {
    const key = popularKey(q);
    const kept = this.peekPopular(q);
    if (kept !== undefined) return kept;
    const failedAt = this.popularFailedAt.get(key);
    if (failedAt !== undefined && Date.now() - failedAt < 60 * 1000) throw new Error('popular unavailable');
    return this.shared(`popular:${key}`, async () => {
      try {
        const { storeService } = await import('../services/store.service');
        const companyId = await storeService.getCompanyId();
        const url = new URL(`${this.baseUrl}/api/popular`);
        url.searchParams.append('companyId', String(companyId));
        url.searchParams.append('storeId', String(q.storeId));
        url.searchParams.append('petType', String(q.petType));
        const breed = q.breedName?.trim();
        if (breed) url.searchParams.append('breedName', breed);
        if (q.serviceId) url.searchParams.append('serviceId', String(q.serviceId));
        if (API_ENVIRONMENT) url.searchParams.append('environment', API_ENVIRONMENT);
        const response = await fetch(url.toString(), { headers: { accept: 'application/json' } });
        if (!response.ok) throw new Error(`popular ${response.status}`);
        const data = await response.json();
        this.popularKept.set(key, { at: Date.now(), data });
        this.popularFailedAt.delete(key);
        return data;
      } catch (e) {
        this.popularFailedAt.set(key, Date.now());
        throw e;
      }
    });
  }

  private readonly popularKept = new Map<string, { at: number; data: unknown }>();
  private readonly popularFailedAt = new Map<string, number>();

  /**
   * The salon's breed list: MPG's breeds and this salon's own (mpgadmin D87,
   * CMS G23), so a breed the salon added shows here and on its pets. Keyed
   * by the business. Without a known business, MPG's list, as before.
   */
  async fetchBreeds(): Promise<Breed[]> {
    const { storeService } = await import('../services/store.service');
    const companyId = await storeService.getCompanyId().catch(() => undefined);
    return this.fetchWithCache<Breed[]>(
      '/api/breeds',
      { companyId, environment: API_ENVIRONMENT },
      30 * 60 * 1000 // 30 minutes cache
    );
  }

  async preloadStoreData(storeId: number): Promise<void> {
    try {
      await Promise.all([
        this.checkCategoryAvailability(storeId),
        this.fetchServiceCategories(storeId, 2), // Dog services
        this.fetchServiceCategories(storeId, 1), // Cat services
        this.fetchBreedGroups(),
        this.fetchBreeds()
      ]);
    } catch (error) {
      console.error(`❌ Preloading failed for store ${storeId}:`, error);
    }
  }

  clearStoreCache(storeId: number): void {
    const stats = cacheService.getStats();
    const storeCacheKeys = stats.keys.filter(key => key.includes(`storeId=${storeId}`));
    
    storeCacheKeys.forEach(key => cacheService.delete(key));
    Array.from(this.menuKept.keys())
      .filter(key => key.includes(`storeId=${storeId}`))
      .forEach(key => this.menuKept.delete(key));
    cacheService.delete(`availability:store:${storeId}`);
    
    console.log(`🧹 Cleared cache for store ${storeId}: ${storeCacheKeys.length} items removed`);
  }

  getCacheStats() {
    return cacheService.getStats();
  }
}

export const optimizedApiService = new OptimizedApiService();
(window as any).optimizedApiService = optimizedApiService; 